import { type FeatureData } from "~/utils/models";
import useMediaQuery from "../../../../../../../common/hooks/useMediaQuery";
import { type ReactNode } from "react";
import { useUnitMode } from "~/domains/userPreferences/hooks/useUnitMode";
import { filterFeatures } from "~/domains/userPreferences/utils/unitLists";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "~/common/utils/screenSizes";
import { TanStackTable } from "~/common/components/Table/TanStackTable";
import { createColumnHelper } from "@tanstack/react-table";
import classNames from "classnames";
import { renderTechValueSrpTable } from "~/utils/renderTechValue";

const getHeaderCell = (feature: FeatureData) => {
  return (
    <div className={classNames(feature.isConverted && "f-info-color")}>
      <span dangerouslySetInnerHTML={{ __html: feature.abbreviation }} />
      {feature.unit && ` (${feature.unit})`}
    </div>
  );
};

const getDataCell = (feature: FeatureData) => {
  return (
    <>
      {feature.values.map((value, index) => (
        <div className={classNames(feature.isConverted && "f-info-color")} key={index}>
          {renderTechValueSrpTable(feature, value)}
        </div>
      ))}
    </>
  );
};
interface TechValuesTableProps {
  features: FeatureData[];
  className?: string;
}

export const SearchTechValueTable = ({ features, className }: TechValuesTableProps) => {
  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);

  const unitMode = useUnitMode();
  features = filterFeatures(features, unitMode);

  if (isDesktop) {
    const data: ReactNode[] = features.map(getDataCell);

    const ch = createColumnHelper<typeof data>();
    const columns = data.map((col, idx) =>
      ch.accessor((data) => data[idx], {
        id: "" + idx,
        header: () => getHeaderCell(features[idx]),
        cell: (cell) => cell.renderValue(),
      }),
    );

    return <TanStackTable className={className} data={[data]} columns={columns} fullwidth={true} />;
  } else {
    const data = features.map((feature) => ({ label: getHeaderCell(feature), val: getDataCell(feature) }));

    const ch = createColumnHelper<(typeof data)[number]>();

    return (
      <div className={className}>
        <TanStackTable
          variant={"stripes"}
          data={data}
          columns={[
            ch.accessor("label", { header: "", cell: (cell) => cell.renderValue() }),
            ch.accessor("val", { header: "", cell: (cell) => cell.renderValue() }),
          ]}
          fullwidth={true}
        />
      </div>
    );
  }
};
